// hooks/useMessaging.js
import { useState, useEffect, useCallback, useRef } from 'react';
import messageListenerService from '../Services/Messaging/MessageListenerService';
import { useAuth } from '../Components/Auth/AuthContext';
import { useUserProfile } from '../Components/Auth/UserProfileContext';
import debug from '../utils/debug';

export const useMessaging = (bookingId = null, conversationData = null) => {
  const { currentUser } = useAuth() || {};
  const { profile } = useUserProfile() || {};
  const [messages, setMessages] = useState([]);
  const [conversationMetadata, setConversationMetadata] = useState(conversationData?.metadata || null);
  const [isTyping, setIsTyping] = useState({});
  const [connectionState, setConnectionState] = useState('disconnected');
  const [unreadCount, setUnreadCount] = useState(0);
  const [sendingMessages, setSendingMessages] = useState(new Map());
  const typingTimeoutRef = useRef(null);
  const unsubscribeRef = useRef([]);

  // Initialize service when user logs in
  useEffect(() => {
    if (!currentUser) return;

    const initializeService = async () => {
      const success = await messageListenerService.initialize(currentUser);
      if (success) {
        setConnectionState('connected');
      }
    };

    initializeService();

    return () => {
      messageListenerService.cleanup();
    };
  }, [currentUser]);

  // Subscribe to global events
  useEffect(() => {
    if (!currentUser) return;

    // Subscribe to metadata updates
    const unsubMetadata = messageListenerService.subscribe('metadata', (data) => {
      if (data.conversationId === bookingId) {
        setConversationMetadata(data.data);
        if (currentUser?.uid && data.data?.unreadCount) {
          setUnreadCount(data.data.unreadCount[currentUser.uid] || 0);
        }
        
        // Update typing indicators
        const typing = {};
        if (data.data?.typing && typeof data.data.typing === 'object' && currentUser?.uid) {
          Object.keys(data.data.typing).forEach(uid => {
            if (uid !== currentUser.uid) {
              typing[uid] = data.data.typing[uid];
            }
          });
        }
        setIsTyping(typing);
      }
    });

    // Subscribe to message updates with deduplication
    const unsubMessages = messageListenerService.subscribe('messages', (data) => {
      if (data.bookingId === bookingId) {
        const msgs = data.messages || [];
        setMessages(prev => {
          // Keep any optimistic messages (ones that are still sending)
          const optimisticMsgs = prev.filter(msg => 
            msg.status === 'sending' && msg.id?.startsWith('temp_')
          );
          
          // Use Set for simpler deduplication
          const messageIds = new Set();
          const uniqueMessages = [];
          
          // Add real messages first
          msgs.forEach(msg => {
            if (!messageIds.has(msg.id)) {
              messageIds.add(msg.id);
              uniqueMessages.push(msg);
            }
          });
          
          // Add optimistic messages that aren't duplicates
          optimisticMsgs.forEach(msg => {
            if (!messageIds.has(msg.id)) {
              messageIds.add(msg.id);
              uniqueMessages.push(msg);
            }
          });
          
          // Sort by createdAt
          return uniqueMessages.sort((a, b) => {
            // Normalize dates for consistent sorting
            const getTime = (date) => {
              if (!date) return 0;
              if (date instanceof Date) return date.getTime();
              if (date.toDate && typeof date.toDate === 'function') return date.toDate().getTime();
              if (date.seconds) return date.seconds * 1000;
              if (typeof date === 'string' || typeof date === 'number') return new Date(date).getTime();
              return 0;
            };
            
            return getTime(a.createdAt) - getTime(b.createdAt);
          });
        });
      }
    });

    // Subscribe to optimistic updates
    const unsubOptimistic = messageListenerService.subscribe('optimisticMessage', (data) => {
      if (data.bookingId === bookingId) {
        debug.log('useMessaging', 'Adding optimistic message', data.message);
        setMessages(prev => [...prev, data.message]);
        setSendingMessages(prev => new Map(prev).set(data.message.id, data.message));
      }
    });

    // Subscribe to message sent confirmations
    const unsubSent = messageListenerService.subscribe('messageSent', (data) => {
      if (data.bookingId === bookingId) {
        // Replace temp message with actual message
        setMessages(prev => prev.map(msg => 
          msg.id === data.tempId ? { ...msg, id: data.actualId, status: 'sent' } : msg
        ));
        setSendingMessages(prev => {
          const newMap = new Map(prev);
          newMap.delete(data.tempId);
          return newMap;
        });
      }
    });

    // Subscribe to message failures
    const unsubFailed = messageListenerService.subscribe('messageFailed', (data) => {
      if (data.bookingId === bookingId) {
        setMessages(prev => prev.map(msg => 
          msg.id === data.tempId ? { ...msg, status: 'failed' } : msg
        ));
      }
    });

    // Subscribe to connection events
    const unsubConnection = messageListenerService.subscribe('connectionFailed', () => {
      setConnectionState('error');
    });

    unsubscribeRef.current = [
      unsubMetadata,
      unsubMessages,
      unsubOptimistic,
      unsubSent,
      unsubFailed,
      unsubConnection
    ];

    return () => {
      unsubscribeRef.current.forEach(unsub => unsub?.());
    };
  }, [currentUser, bookingId]);

  // Listen to specific conversation
  useEffect(() => {
    if (!bookingId || !currentUser) return;

    messageListenerService.listenToConversation(bookingId);
    messageListenerService.setActiveConversation(bookingId);

    return () => {
      messageListenerService.setActiveConversation(null);
    };
  }, [bookingId, currentUser]);

  // Send message
  const sendMessage = useCallback(async (content, attachment = null) => {
    if (!bookingId || !currentUser) return false;
    if (!content?.trim() && !attachment) return false;

    // Determine organiserUID and talentUID from conversationData or metadata
    let organiserUID, talentUID;
    
    if (conversationData) {
      organiserUID = conversationData.organiserUID || conversationData.bookingData?.organiserUID;
      talentUID = conversationData.talentUID || conversationData.bookingData?.talentUID;
    }
    
    // Fallback to metadata participants if available
    if (!organiserUID || !talentUID) {
      if (conversationMetadata?.participants?.length === 2) {
        // We need to determine which is which from other context
        // This is a fallback and might need additional logic
        const participants = conversationMetadata.participants;
        organiserUID = organiserUID || participants[0];
        talentUID = talentUID || participants[1];
      }
    }

    if (!organiserUID || !talentUID) {
      debug.error('useMessaging', 'Cannot determine organiserUID or talentUID', { 
        bookingId, 
        hasMetadata: !!conversationMetadata,
        hasConversationData: !!conversationData 
      });
      return false;
    }

    // Get sender name from profile or currentUser
    const senderName = profile?.displayName || 
                      profile?.profileDisplayName ||
                      (profile?.firstName && profile?.lastName ? 
                       `${profile.firstName} ${profile.lastName}` : 
                       currentUser?.displayName || 
                       'User');
    
    const messageData = {
      senderId: currentUser.uid,
      senderName,  // Add sender name
      organiserUID,
      talentUID,
      bookingId,
      content: content?.trim() || '',
      attachment,
      read: false
    };

    const messageId = await messageListenerService.sendMessage(bookingId, messageData);
    return messageId !== null;
  }, [bookingId, currentUser, profile, conversationMetadata, conversationData]);

  // Mark messages as read
  const markAsRead = useCallback(async () => {
    if (!messages || !currentUser || !bookingId) return;

    const unreadMessages = messages.filter(
      msg => msg.senderId !== currentUser.uid && !msg.read
    );

    if (unreadMessages.length > 0) {
      await messageListenerService.markMessagesAsRead(bookingId, unreadMessages);
    }
  }, [messages, currentUser, bookingId]);

  // Handle typing indicator
  const handleTyping = useCallback((isTyping) => {
    if (!bookingId || !currentUser) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set typing status
    messageListenerService.setTypingStatus(bookingId, isTyping);

    // Auto-clear typing after 3 seconds
    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        messageListenerService.setTypingStatus(bookingId, false);
      }, 3000);
    }
  }, [bookingId, currentUser]);

  // Get total unread count
  const getTotalUnreadCount = useCallback(() => {
    return messageListenerService.getTotalUnreadCount();
  }, []);

  // Retry failed message
  const retryMessage = useCallback(async (messageId) => {
    // Guard against undefined messages array
    if (!messages || !Array.isArray(messages)) {
      debug.warn('useMessaging', 'retryMessage called but messages array is not available');
      return false;
    }
    
    const message = messages.find(msg => msg.id === messageId);
    if (!message || message.status !== 'failed') return false;

    // Remove failed message
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
    
    // Resend
    return await sendMessage(message.content, message.attachments);
  }, [messages, sendMessage]);

  // Always return a valid object with all expected properties
  try {
    return {
      // State
      messages,
      conversationMetadata,
      isTyping,
      connectionState,
      unreadCount,
      sendingMessages: Array.from(sendingMessages.values()),
      
      // Actions
      sendMessage,
      markAsRead,
      handleTyping,
      getTotalUnreadCount,
      retryMessage,
      
      // Computed
      isConnected: connectionState === 'connected',
      hasUnread: unreadCount > 0,
      otherParticipant: Array.isArray(conversationMetadata?.participants) 
        ? conversationMetadata.participants.find(p => p !== currentUser?.uid)
        : null
    };
  } catch (error) {
    debug.error('useMessaging', 'Error in hook return', error);
    // Return safe defaults if there's any error
    return {
      messages: [],
      conversationMetadata: null,
      isTyping: {},
      connectionState: 'disconnected',
      unreadCount: 0,
      sendingMessages: [],
      sendMessage: async () => false,
      markAsRead: async () => {},
      handleTyping: () => {},
      getTotalUnreadCount: () => 0,
      retryMessage: async () => false,
      isConnected: false,
      hasUnread: false,
      otherParticipant: null
    };
  }
};

// Hook for global message notifications
export const useGlobalMessaging = () => {
  const { currentUser } = useAuth() || {};
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [recentMessages, setRecentMessages] = useState([]);
  const [connectionState, setConnectionState] = useState('disconnected');

  useEffect(() => {
    if (!currentUser) return;

    const initializeService = async () => {
      const success = await messageListenerService.initialize(currentUser);
      if (success) {
        setConnectionState('connected');
        setTotalUnreadCount(messageListenerService.getTotalUnreadCount());
      }
    };

    initializeService();

    // Subscribe to all metadata updates
    const unsubscribe = messageListenerService.subscribe('metadata', (data) => {
      setTotalUnreadCount(messageListenerService.getTotalUnreadCount());
      
      // Track recent messages
      if (data.data?.lastMessage && currentUser?.uid) {
        setRecentMessages(prev => {
          const updated = [...prev];
          const index = updated.findIndex(m => m.conversationId === data.conversationId);
          
          const newMessage = {
            conversationId: data.conversationId,
            ...data.data.lastMessage,
            unreadCount: data.data.unreadCount?.[currentUser.uid] || 0
          };
          
          if (index >= 0) {
            updated[index] = newMessage;
          } else {
            updated.unshift(newMessage);
          }
          
          return updated.slice(0, 10); // Keep only 10 most recent
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser]);

  return {
    totalUnreadCount,
    recentMessages,
    connectionState,
    isConnected: connectionState === 'connected'
  };
};
